import { atom } from 'jotai';
import { SortDirection, SortingType } from '../../components/atoms/SortingMenu';
import { useEffect, useState } from 'react';
import { inspectionDB } from '../../data/database.structure';
import DateHelpers from '../../helpers/dateHelpers';
import {
  AssetModel,
  InspectionCategoryModel,
  InspectionModel,
} from '../../data/types';
import { atomWithMutation } from 'jotai-tanstack-query';
import { EMutationKey } from '../mutationKeys';
import useMergedState from '../../hooks/useMergedState';
import { v4 } from 'uuid';
import { min } from 'date-fns';
import { inspectionRecordService } from '../../services/inspection/inspectionRecordService';

interface NewInspectionState {
  searchValue?: string;
  sortMenuOpen: boolean;
  sortConfig: {
    key: SortingType;
    direction?: SortDirection;
  };
}

export const useAssetPage = (initialPage = 1) => {
  const [pagination, setState] = useState(initialPage);

  const increment = () => setState((prev) => prev + 1);

  const decrement = () => {
    if (pagination > 1) {
      setState((prev) => prev - 1);
    }
  };

  const reset = () => setState(1);

  useEffect(() => {
    return () => {
      // Cleanup function to reset state when the component is unmounted
      setState(1);
    };
  }, []);

  return { pagination, increment, decrement, reset };
};

export const useNewInspectionHelper = () => {
  const [state, setState] = useMergedState<NewInspectionState>({
    searchValue: undefined,
    sortMenuOpen: false,
    sortConfig: { key: SortingType.alphabetical, direction: undefined },
  });

  return { state, setState };
};

export const createNewInspection = atomWithMutation(() => ({
  mutationKey: [EMutationKey.createInspection],
  mutationFn: async ({
    inspectionId,
    inspectorName,
    assetId,
    siteId,
    mineLocationId,
    equipmentExternalId,
  }: {
    inspectionId: string;
    inspectorName: string;
    siteId: number;
    assetId?: number;
    mineLocationId?: number;
    equipmentExternalId?: string;
  }) => {
    const inspectionCategories: InspectionCategoryModel[] = [];

    const inspectionCategoryUndercarriage =
      await inspectionDB.inspectionCategories
        .where('name')
        .equals('All Categories')
        .first();

    if (inspectionCategoryUndercarriage !== undefined) {
      inspectionCategories.push(inspectionCategoryUndercarriage);
    }

    const inspectionTypeCondition = await inspectionDB.inspectionTypes
      .where('name')
      .equals('AllTypes')
      .first();

    const inspection: InspectionModel = {
      assetId: assetId ?? -1,
      mineLocationId: mineLocationId ?? -1,
      externalId: inspectionId,
      id: 0,
      inspectionStatus: '-1',
      siteId: siteId,
      equipmentExternalId: equipmentExternalId,
      name: `Inspection ${inspectionId}`,
      inspectionStatusId: 0,
      inspectionDate: new Date(),
      completed: false,
      reopened: false,
      inspectorName: inspectorName,
      isSynced: false,
      inspectionCategoryTypes: [
        ...inspectionCategories.map((inspectionCategory) => ({
          externalId: v4(),
          type: inspectionTypeCondition!.name,
          category: inspectionCategory.name,
          inspectionId: 0,
          typeId: inspectionTypeCondition!.id,
          categoryId: inspectionCategory.id,
          isSynced: false,
          observations: [],
          id: 0,
        })),
      ],
    };

    await inspectionRecordService.create(inspection);
    return inspection;
  },
  onError: (e) => {
    console.log(`create inspection error ${e}`);
  },
  networkMode: 'always',
}));
